/**
 * Online Food Ordering Mobile Cart Bar
 * Modern edge-to-edge sticky bottom cart bar for mobile
 */

import { ShoppingBag as BagIcon, LocalShipping as DeliveryIcon } from '@mui/icons-material';
import { Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useOrderFees } from '@/hooks/useOrderFees';
import { useAppStore } from '@/store/useAppStore';
import { useCartItemCount, useCartStore, useCartSubtotal } from '@/store/useCartStore';

export default function MobileCartBar() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const { t } = useTranslation();

  const items = useCartStore((state) => state.items);
  const orderType = useCartStore((state) => state.orderType);
  const toggleCartDrawer = useAppStore((state) => state.toggleCartDrawer);

  const subtotal = useCartSubtotal();
  const itemCount = useCartItemCount();

  // Use centralized fee calculation for consistent free delivery messaging
  const { hasFreeDelivery, amountForFreeDelivery } = useOrderFees();

  // Don't show on desktop, when cart is empty, or on item detail pages
  const isItemDetailPage = location.pathname.startsWith('/item/');
  if (!isMobile || items.length === 0 || isItemDetailPage) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        exit={{ y: 100 }}
        transition={{ type: 'spring', damping: 25, stiffness: 500 }}
        style={{
          position: 'fixed',
          bottom: 56, // Above mobile nav
          left: 0,
          right: 0,
          zIndex: 1100,
        }}
      >
        {/* Upsell Banner - edge to edge with solid background */}
        {orderType === 'delivery' && !hasFreeDelivery && (
          <Box
            sx={{
              backgroundColor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
              borderTop: `2px solid ${theme.palette.success.main}`,
              px: 2,
              py: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DeliveryIcon sx={{ fontSize: 18, color: 'success.main' }} />
              <Typography variant="caption" fontWeight={600} color="text.primary">
                {t('cart.upsell_delivery', { amount: amountForFreeDelivery.toFixed(2) })}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Free delivery achieved */}
        {orderType === 'delivery' && hasFreeDelivery && (
          <Box
            sx={{
              backgroundColor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
              borderTop: `2px solid ${theme.palette.success.main}`,
              px: 2,
              py: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DeliveryIcon sx={{ fontSize: 18, color: 'success.main' }} />
              <Typography variant="caption" fontWeight={600} color="success.main">
                {t('cart.free_delivery')}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Basket button - edge to edge, no radius */}
        <Button
          fullWidth
          onClick={toggleCartDrawer}
          sx={{
            py: 1.5,
            px: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: theme.palette.primary.main,
            color: 'white',
            textTransform: 'none',
            borderRadius: 0,
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
            },
          }}
        >
          {/* Left - item count badge */}
          <Box
            sx={{
              backgroundColor: 'rgba(255,255,255,0.2)',
              borderRadius: 1,
              px: 1,
              py: 0.5,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <BagIcon sx={{ fontSize: 16 }} />
            <Typography variant="body2" fontWeight={700} sx={{ color: 'white' }}>
              {itemCount}
            </Typography>
          </Box>

          {/* Center - Basket text */}
          <Typography variant="subtitle1" fontWeight={600} sx={{ color: 'white' }}>
            {t('cart.basket')}
          </Typography>

          {/* Right - price */}
          <Typography variant="subtitle1" fontWeight={700} sx={{ color: 'white' }}>
            £{subtotal.toFixed(2)}
          </Typography>
        </Button>
      </motion.div>
    </AnimatePresence>
  );
}
